//@flow
import * as Immutable from 'immutable';
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo
import type { FpOutletVisitModelType } from './FpOutletVisit';

import { PlanStatusEnum } from 'common/enums/PlanEnums';
import type { FpSalesbeatPlanModelType } from './FpSalesbeatplan';

export type PjPlanStatusTypeEnum = 'ACTIVE' | 'INACTIVE';
export type FpPjPlanModelType = {
    clientId: string,
    id: string,
    name: string,
    code: string,
    auditMetadata: FpPlanAuditMetadataModelType,
    status: PjPlanStatusTypeEnum,
    customProperties: { [key: string]: string },
    homebaseId: string,
    outletsInfo: Object,
    ridersInfo: Object,
    configuration: Object,
    settings: {
        outletFieldsToCopyToTask: Array<string>,
        splitBatch: boolean,
        splitParam: string,
        splitOnColumn: string
    },
    metricsReport: ?Object,
    outletVisits: Array<FpOutletVisitModelType>,
    batch: {
        clientId: string,
        batchId: string
    },
    plan: {
        clientId: string,
        batchId: string,
        planId: string
    },
    salesbeatPlan: ?FpSalesbeatPlanModelType,
    teamId: {
        clientId: string,
        teamId: string
    },
    existingPlan: ?Object,
    distributionBatch: ?Object,
    distributionPlan: ?Object,
    pjPlanInsights: {
        planDetails: {
            existingPlan: ?Object,
            currentPlan: ?Object,
            improvementsMap: ?Object
        },
        keySummary: {
            existingPlan: ?Object,
            currentPlan: ?Object,
            improvementsMap: ?Object
        },
        exportMetrics: {
            existingPlan: ?Object,
            currentPlan: Object,
            improvementsMap: Object
        }
    },
    planStatus: PlanStatusEnum,
    statusUpdates: Array<any>,
    progress: Array<any>,
    billable: boolean,
    notes: Array<any>
};
export type FpPlanAuditMetadataModelType = {
    createdBy: {
        id: string
    },
    createdOn: string,
    createdOnDate: ?string,
    updatedBy: {
        id: string
    },
    updatedOn: string,
    updatedOnDate: ?string
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class FpPjPlan extends ImmutableModel {
    static fromJS(json: FpPjPlanModelType): FpPjPlan {
        const state: Object = Object.assign({}, json);
        state.auditMetadata = FpPlanAuditMetadata.fromJS(state.auditMetadata);
        state.customProperties = Immutable.Map(state.customProperties);
        state.outletVisits = Immutable.List(state.outletVisits).map(item =>
            FpOutletVisit.fromJS(item)
        );
        state.salesbeatPlan = state.salesbeatPlan
            ? FpSalesbeatPlan.fromJS(state.salesbeatPlan)
            : state.salesbeatPlan;
        state.statusUpdates = Immutable.List(state.statusUpdates);
        state.progress = Immutable.List(state.progress);
        state.notes = Immutable.List(state.notes);
        return new this(Immutable.Map(state));
    }

    toJS(): FpPjPlanModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            name: this.name,
            code: this.code,
            auditMetadata: this.auditMetadata.toJS(),
            status: this.status,
            customProperties: this.customProperties.toObject(),
            homebaseId: this.homebaseId,
            outletsInfo: this.outletsInfo,
            ridersInfo: this.ridersInfo,
            configuration: this.configuration,
            settings: this.settings,
            metricsReport: this.metricsReport,
            outletVisits: this.outletVisits.toArray().map(item => item.toJS()),
            batch: this.batch,
            plan: this.plan,
            salesbeatPlan: this.salesbeatPlan
                ? this.salesbeatPlan.toJS()
                : this.salesbeatPlan,
            teamId: this.teamId,
            existingPlan: this.existingPlan,
            distributionBatch: this.distributionBatch,
            distributionPlan: this.distributionPlan,
            pjPlanInsights: this.pjPlanInsights,
            planStatus: this.planStatus,
            statusUpdates: this.statusUpdates.toArray(),
            progress: this.progress.toArray(),
            billable: this.billable,
            notes: this.notes.toArray()
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get auditMetadata(): FpPlanAuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: FpPlanAuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get status(): PjPlanStatusTypeEnum {
        return this._state.get('status');
    }

    setStatus(status: PjPlanStatusTypeEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get homebaseId(): string {
        return this._state.get('homebaseId');
    }

    setHomebaseId(homebaseId: string): this {
        return this.clone(this._state.set('homebaseId', homebaseId));
    }

    get outletsInfo(): Object {
        return this._state.get('outletsInfo');
    }

    setOutletsInfo(outletsInfo: Object): this {
        return this.clone(this._state.set('outletsInfo', outletsInfo));
    }

    get ridersInfo(): Object {
        return this._state.get('ridersInfo');
    }

    setRidersInfo(ridersInfo: Object): this {
        return this.clone(this._state.set('ridersInfo', ridersInfo));
    }

    get configuration(): Object {
        return this._state.get('configuration');
    }

    setConfiguration(configuration: Object): this {
        return this.clone(this._state.set('configuration', configuration));
    }

    get settings(): {
        outletFieldsToCopyToTask: Array<string>,
        splitBatch: boolean,
        splitParam: string,
        splitOnColumn: string
    } {
        return this._state.get('settings');
    }

    setSettings(settings: {
        outletFieldsToCopyToTask: Array<string>,
        splitBatch: boolean,
        splitParam: string,
        splitOnColumn: string
    }): this {
        return this.clone(this._state.set('settings', settings));
    }

    get metricsReport(): ?Object {
        return this._state.get('metricsReport');
    }

    setMetricsReport(metricsReport: ?Object): this {
        return this.clone(this._state.set('metricsReport', metricsReport));
    }

    get outletVisits(): Immutable.List<FpOutletVisit> {
        return this._state.get('outletVisits');
    }

    setOutletVisits(outletVisits: Immutable.List<FpOutletVisit>): this {
        return this.clone(this._state.set('outletVisits', outletVisits));
    }

    get batch(): {
        clientId: string,
        batchId: string
    } {
        return this._state.get('batch');
    }

    setBatch(batch: { clientId: string, batchId: string }): this {
        return this.clone(this._state.set('batch', batch));
    }

    get plan(): {
        clientId: string,
        batchId: string,
        planId: string
    } {
        return this._state.get('plan');
    }

    setPlan(plan: { clientId: string, batchId: string, planId: string }): this {
        return this.clone(this._state.set('plan', plan));
    }

    get salesbeatPlan(): ?FpSalesbeatPlan {
        return this._state.get('salesbeatPlan');
    }

    setSalesbeatPlan(salesbeatPlan: ?FpSalesbeatPlan): this {
        return this.clone(this._state.set('salesbeatPlan', salesbeatPlan));
    }

    get teamId(): {
        clientId: string,
        teamId: string
    } {
        return this._state.get('teamId');
    }

    setTeamId(teamId: { clientId: string, teamId: string }): this {
        return this.clone(this._state.set('teamId', teamId));
    }

    get existingPlan(): ?Object {
        return this._state.get('existingPlan');
    }

    setExistingPlan(existingPlan: ?Object): this {
        return this.clone(this._state.set('existingPlan', existingPlan));
    }

    get distributionBatch(): ?Object {
        return this._state.get('distributionBatch');
    }

    setDistributionBatch(distributionBatch: ?Object): this {
        return this.clone(
            this._state.set('distributionBatch', distributionBatch)
        );
    }

    get distributionPlan(): ?Object {
        return this._state.get('distributionPlan');
    }

    setDistributionPlan(distributionPlan: ?Object): this {
        return this.clone(
            this._state.set('distributionPlan', distributionPlan)
        );
    }

    get pjPlanInsights(): {
        planDetails: {
            existingPlan: ?Object,
            currentPlan: ?Object,
            improvementsMap: ?Object
        },
        keySummary: {
            existingPlan: ?Object,
            currentPlan: ?Object,
            improvementsMap: ?Object
        },
        exportMetrics: {
            existingPlan: ?Object,
            currentPlan: Object,
            improvementsMap: Object
        }
    } {
        return this._state.get('pjPlanInsights');
    }

    setPjPlanInsights(pjPlanInsights: {
        planDetails: {
            existingPlan: ?Object,
            currentPlan: ?Object,
            improvementsMap: ?Object
        },
        keySummary: {
            existingPlan: ?Object,
            currentPlan: ?Object,
            improvementsMap: ?Object
        },
        exportMetrics: {
            existingPlan: ?Object,
            currentPlan: Object,
            improvementsMap: Object
        }
    }): this {
        return this.clone(this._state.set('pjPlanInsights', pjPlanInsights));
    }

    get planStatus(): PlanStatusEnum {
        return this._state.get('planStatus');
    }

    setPlanStatus(planStatus: PlanStatusEnum): this {
        return this.clone(this._state.set('planStatus', planStatus));
    }

    get statusUpdates(): Immutable.List<any> {
        return this._state.get('statusUpdates');
    }

    setStatusUpdates(statusUpdates: Immutable.List<any>): this {
        return this.clone(this._state.set('statusUpdates', statusUpdates));
    }

    get progress(): Immutable.List<any> {
        return this._state.get('progress');
    }

    setProgress(progress: Immutable.List<any>): this {
        return this.clone(this._state.set('progress', progress));
    }

    get billable(): boolean {
        return this._state.get('billable');
    }

    setBillable(billable: boolean): this {
        return this.clone(this._state.set('billable', billable));
    }

    get notes(): Immutable.List<any> {
        return this._state.get('notes');
    }

    setNotes(notes: Immutable.List<any>): this {
        return this.clone(this._state.set('notes', notes));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class FpPlanAuditMetadata extends ImmutableModel {
    static fromJS(json: FpPlanAuditMetadataModelType): FpPlanAuditMetadata {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): FpPlanAuditMetadataModelType {
        return {
            createdBy: this.createdBy,
            createdOn: this.createdOn,
            createdOnDate: this.createdOnDate,
            updatedBy: this.updatedBy,
            updatedOn: this.updatedOn,
            updatedOnDate: this.updatedOnDate
        };
    }

    get createdBy(): { id: string } {
        return this._state.get('createdBy');
    }

    setCreatedBy(createdBy: { id: string }): this {
        return this.clone(this._state.set('createdBy', createdBy));
    }

    get createdOn(): string {
        return this._state.get('createdOn');
    }

    setCreatedOn(createdOn: string): this {
        return this.clone(this._state.set('createdOn', createdOn));
    }

    get createdOnDate(): ?string {
        return this._state.get('createdOnDate');
    }

    setCreatedOnDate(createdOnDate: ?string): this {
        return this.clone(this._state.set('createdOnDate', createdOnDate));
    }

    get updatedBy(): { id: string } {
        return this._state.get('updatedBy');
    }

    setUpdatedBy(updatedBy: { id: string }): this {
        return this.clone(this._state.set('updatedBy', updatedBy));
    }

    get updatedOn(): string {
        return this._state.get('updatedOn');
    }

    setUpdatedOn(updatedOn: string): this {
        return this.clone(this._state.set('updatedOn', updatedOn));
    }

    get updatedOnDate(): ?string {
        return this._state.get('updatedOnDate');
    }

    setUpdatedOnDate(updatedOnDate: ?string): this {
        return this.clone(this._state.set('updatedOnDate', updatedOnDate));
    }
}
